import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Equipment } from 'app/shared/models/equipment.model';

import { ProcessTypeService } from 'app/core/services/process-type.service';
import { ProcessType } from 'app/shared/models/process-type.model';

import { LineService } from 'app/core/services/line.service';
import { Line } from 'app/shared/models/line.model';

import { ModalComponent } from 'app/shared/components';
import { PermissionService } from "@app/authentication/services/permission.service";

/**
 * Modal form for creating / updating {@link Equipment} objects.
 */
@Component({
    selector: 'app-equipment-create-modal',
    templateUrl: './equipment-create-modal.component.html',
    styleUrls: ['./equipment-create-modal.component.scss']
})
export class EquipmentCreateModalComponent implements OnInit {

    @ViewChild(ModalComponent, {static: true}) modal: ModalComponent;

    @Output('onCreate')
    onCreate: EventEmitter<any> = new EventEmitter();
    @Output('onUpdate')
    onUpdate: EventEmitter<any> = new EventEmitter();
    @Output('onDelete')
    onDelete: EventEmitter<any> = new EventEmitter();

    @Output() updateLine = new EventEmitter<any>();

    @Input() isLoading;

    public equipment: Equipment = new Equipment();

    equipmentForm: UntypedFormGroup;

    previousEquipmentName = '';

    error = false;

    validationErrors;
    showValidations = false;

    // Array that keeps the process types which can be chosen in the dropdown field
    processTypes: ProcessType[] = [];

    // Array that keeps the out of use lines which can be chosen in the dropdown field
    lines: Line[] = [];
    // Indicated whether the modal should be used in duplicate mode or not
    duplicateMode = false;

    static mapProcessTypes(processTypes = []) {
        return processTypes.map(prc => prc);
    }

    static lineValidator(control: UntypedFormControl): { [s: string]: boolean } {
        if (control.value === 0) {
            return {'required': true};
        }
        return null;
    }

    constructor(private processTypeService: ProcessTypeService,
                private lineService: LineService,
                private permissionService: PermissionService) {
    }

    ngOnInit() {
        this.onFormInit();
        if (this.shouldRenderModifiers()) {
            this.getLines();
            this.getProcessTypes();
        }
    }

    onFormInit() {
        const equipmentId = 0;
        const equipmentName = '';
        const equipmentDescription = '';
        const equipmentMESId = '';
        const equipmentLineId = 0;
        const equipmentProcessTypes = [];

        this.equipmentForm = new UntypedFormGroup({
            id: new UntypedFormControl(equipmentId),
            name: new UntypedFormControl(equipmentName, Validators.required),
            description: new UntypedFormControl(equipmentDescription, [Validators.required]),
            MESId: new UntypedFormControl(equipmentMESId),
            lineId: new UntypedFormControl(equipmentLineId, [Validators.required, EquipmentCreateModalComponent.lineValidator.bind(this)]),
            processTypes: new UntypedFormControl(equipmentProcessTypes)
        });
    }

    /**
     * @param {any} equipment
     */
    open(equipment = new Equipment(), duplicate = false) {
        this.resetForm();
        this.loadFormValue(equipment);
        this.duplicateMode = duplicate;
        this.modal.open();
    }

    close() {
        this.modal.close();
    }

    loadFormValue(equipment) {
        this.equipment = equipment;
        this.previousEquipmentName = equipment.name;
        this.equipmentForm.setValue({
            id: equipment.id,
            name: equipment.name,
            description: equipment.description,
            MESId: equipment.MESId,
            lineId: (equipment.line.id === 0) ? 0 : equipment.line,
            processTypes: EquipmentCreateModalComponent.mapProcessTypes(this.equipment.processTypes)
        });
    }

    createEquipment() {
        if (this.equipmentForm.invalid) {
            this.showValidations = true;
            return;
        }
        this.equipmentForm.value.lineId = this.equipment.line.id;
        this.onCreate.emit(this.equipmentForm.value);
    }

    updateEquipment() {
        if (this.equipmentForm.invalid) {
            this.showValidations = true;
            return;
        }
        this.equipmentForm.value.lineId = this.equipment.line.id;
        this.onUpdate.emit({previousEquipmentName: this.previousEquipmentName, newEquipment: this.equipmentForm.value});
    }

    deleteEquipment() {
        this.onDelete.emit(this.equipmentForm.value);
    }

    resetForm() {
        this.showValidations = false;
        this.validationErrors = {};
        this.equipment = new Equipment();
        this.previousEquipmentName = '';
        this.equipmentForm.reset(this.equipment);
        this.duplicateMode = false;
    }

    onEquipmentChange(line) {
        this.equipment.line = line;
    }

    private getLines() {
        // Observable for retrieving lines data
        this.lineService.getAllLines().subscribe(
            (data: any) => {
                this.lines = data.items;
            },
            (error: any) => {
            }
        );
    }

    private getProcessTypes() {
        // Observable for retrieving process types data
        this.processTypeService.getAllProcessTypes().subscribe(
            (data: any) => {
                this.processTypes = data.processTypes;
            },
            (error: any) => {
            }
        );
    }

    isEquipmentInUse() {
        return this.equipment.inUse;
    }

    get isEditMode() {
        return !!this.equipment.id && !this.duplicateMode;
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_EQUIPMENT');
    }

}
